import { useState, useEffect, useCallback } from "react";
import { authFetch, API } from "@/App";
import { Search } from "lucide-react";

export default function FinishedGoodsPage() {
  const [goods, setGoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const loadGoods = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/factory/finished-goods`);
      const json = await res.json();
      if (json.success) setGoods(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadGoods(); }, [loadGoods]);

  const filtered = goods.filter((g) => g.product?.toLowerCase().includes(search.toLowerCase()));
  const totalBags = goods.reduce((a, g) => a + (g.stock_bags || 0), 0);
  const totalMT = goods.reduce((a, g) => a + (g.stock_mt || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="finished-goods-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Finished Goods</h1>
        <p className="text-slate-400 text-sm mt-1">Packaged inventory ready for dispatch</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid="fg-total-bags">
          <p className="text-slate-400 text-xs uppercase">Total Bags</p>
          <p className="text-white text-2xl font-bold mt-1">{totalBags.toLocaleString()}</p>
        </div>
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid="fg-total-mt">
          <p className="text-slate-400 text-xs uppercase">Total MT</p>
          <p className="text-white text-2xl font-bold mt-1">{totalMT.toLocaleString()}</p>
        </div>
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid="fg-products">
          <p className="text-slate-400 text-xs uppercase">Product Lines</p>
          <p className="text-white text-2xl font-bold mt-1">{goods.length}</p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input type="text" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-600" data-testid="fg-search" />
      </div>

      <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl overflow-hidden">
        <table className="w-full" data-testid="finished-goods-table">
          <thead>
            <tr className="border-b border-emerald-900/30">
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Product</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">SKU</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase">Stock (Bags)</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase">Stock (MT)</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase">Min Stock</th>
              <th className="text-center px-4 py-3 text-xs text-slate-400 uppercase">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((g) => (
              <tr key={g.item_id} className="border-b border-emerald-900/20 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-sm text-white font-medium">{g.product}</td>
                <td className="px-4 py-3 text-sm text-slate-400">{g.sku}</td>
                <td className="px-4 py-3 text-sm text-white text-right">{g.stock_bags?.toLocaleString()}</td>
                <td className="px-4 py-3 text-sm text-white text-right">{g.stock_mt?.toLocaleString()}</td>
                <td className="px-4 py-3 text-sm text-slate-400 text-right">{g.min_stock?.toLocaleString()}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${g.status === "LOW" ? "bg-amber-600/20 text-amber-400" : "bg-emerald-600/20 text-emerald-400"}`}>{g.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
